/**
 * csv.ts — build RFC 4180 CSV that survives Excel and the GST portal.
 *
 * The reports layer hand-rolls PDF but has no CSV, though accountants ask for CSV
 * far more often. The trap CSV always springs is escaping: a customer named
 * "Sharma, Traders", a note with a newline, an invoice number that Excel reads as
 * a date. This module is the one place that gets it right, so no report has to.
 */

export type CsvCell = string | number | null | undefined;

/**
 * Quote a single field per RFC 4180.
 *
 * A field is quoted when it contains a comma, quote, or newline; embedded quotes
 * are doubled. A leading `=`, `+`, `-`, or `@` is prefixed with a single quote so
 * spreadsheets do not interpret it as a formula (CSV-injection guard) — this
 * matters because report cells carry user-entered names and notes.
 */
export function csvField(value: CsvCell): string {
  if (value === null || value === undefined) return '';
  let s = String(value);

  if (/^[=+\-@]/.test(s)) s = `'${s}`;

  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export interface CsvColumn<Row> {
  header: string;
  value: (row: Row) => CsvCell;
}

/**
 * Build a CSV string from typed rows and column definitions.
 *
 * Rows are separated by CRLF (the RFC 4180 line ending Excel expects) and the
 * file is prefixed with a UTF-8 BOM so Excel renders ₹ and other non-ASCII
 * correctly instead of mojibake.
 */
export function toCsv<Row>(rows: readonly Row[], columns: readonly CsvColumn<Row>[]): string {
  const BOM = '﻿';
  const headerLine = columns.map((c) => csvField(c.header)).join(',');
  const body = rows.map((row) => columns.map((c) => csvField(c.value(row))).join(','));
  return BOM + [headerLine, ...body].join('\r\n');
}
